import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import { Notifications } from './components/Layout/Notifications'
// Removed unused ProtectedRoute and GuestRoute; loaders handle auth
import { createBrowserRouter, RouterProvider, redirect } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { HelmetProvider } from 'react-helmet-async'
import { AuthPage } from '@/components/Auth/AuthPage'
import { ProtectedApp } from '@/routes/ProtectedApp'
import { PrivacyPolicy } from '@/routes/PrivacyPolicy'
import { TermsOfService } from '@/routes/TermsOfService'
import { Pricing } from '@/routes/Pricing'
import { Contact } from '@/routes/Contact'
import { initializeTheme } from './lib/theme'
// Removed Toaster; using global Notifications bottom-left
import { AcceptInvite } from '@/components/Auth/AcceptInvite'
import { ForgotPasswordPage } from '@/components/Auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/components/Auth/ResetPasswordPage'
import { MagicLinkVerify } from '@/components/Auth/MagicLinkVerify'
import { RegisterPage } from '@/components/Auth/RegisterPage'
import { VerifyEmail } from '@/components/Auth/VerifyEmail'
import { apiFetch } from '@/lib/api'
import { AppErrorBoundary } from '@/components/Layout/AppErrorBoundary'
import { RouteErrorBoundary } from '@/components/Layout/RouteErrorBoundary'
import { storageKey, HOME_ROUTE, appConfig } from '@/config/app'
import { WorkspaceFinder } from '@/routes/roros/WorkspaceFinder'
import { initSentry } from '@/lib/sentry'

function trackNavigation(_path: string) {}

// AC-M4-2: init Sentry before any rendering so errors are captured from the start
initSentry()

// Initialize theme on app load
initializeTheme()

// Initialize VConsole mobile console — opt-in only via env var
// Useful for debugging on mobile devices where dev tools aren't available
const shouldEnableVConsole = import.meta.env.VITE_ENABLE_MOBILE_DEBUG === 'true';

if (shouldEnableVConsole) {
  import('vconsole').then((VConsole) => {
    new VConsole.default({
      theme: 'dark',
      defaultPlugins: ['system', 'network', 'element', 'storage'],
      maxLogNumber: 1000,
      onReady: () => {
        console.log('📱 VConsole mobile debugger initialized');
        console.log('📱 Tap the green button to open the mobile console');
      }
    });
  }).catch(error => {
    console.warn('Failed to load VConsole mobile debugger:', error);
  });
}


const router = createBrowserRouter([
  {
    // AC-PROD-4: Workspace-finder page served at the apex domain
    path: '/workspace-finder',
    element: <WorkspaceFinder />,
  },
  {
    path: '/',
    errorElement: <RouteErrorBoundary />,
    loader: async () => {
      trackNavigation('/');

      // AC-PROD-4: If running on the apex domain (e.g. https://roros.ai),
      // redirect to the workspace-finder page instead of the normal auth flow.
      if (appConfig.tenantBaseDomain) {
        const hostname = window.location.hostname.toLowerCase();
        const base = appConfig.tenantBaseDomain.toLowerCase();
        if (hostname === base || hostname === `www.${base}`) {
          throw redirect('/workspace-finder');
        }
      }

      try {
        const res = await apiFetch('/api/session/bootstrap', { skipRefresh: true });
        if (res.ok) {
          const payload = await res.json().catch(() => ({ data: {} }));

          // Session drift guard: if the bootstrapped user differs from the
          // currently stored user, wipe session-scoped state before writing
          // anything new.
          {
            const { useAuthStore } = await import('@/stores/authStore');
            const prevUserId = useAuthStore.getState().user?.id;
            const nextUserId = payload?.data?.user?.id;
            if (prevUserId && nextUserId && prevUserId !== nextUserId) {
              if (process.env.NODE_ENV === 'development') console.log('[ROOT LOADER] User drift detected, resetting session state', { prev: prevUserId, next: nextUserId });
              const { resetSessionScopedState } = await import('@/lib/sessionReset');
              resetSessionScopedState();
            }
          }

          const active = payload?.data?.activeOrgId || null;
          // Persist active org for services that read from localStorage (for X-Organization-Id header)
          try {
            if (active) localStorage.setItem(storageKey('active_org_id'), active);
          } catch {}
          // Super admin goes straight to admin panel
          const isSuperAdmin = Boolean(payload?.data?.user?.isSuperAdmin);
          if (isSuperAdmin) {
            throw redirect('/admin');
          }
          // User is logged in — redirect to HOME_ROUTE
          // Product-specific onboarding (e.g., ROROS Launchpad) is handled by route guards,
          // not by the boilerplate. No onboarding_completed check needed here.
          // HOME_ROUTE === '/' is a special case: we're already at '/', so
          // skip the redirect and render ProtectedApp inline. Saves one
          // navigation on first paint. Any other HOME_ROUTE value falls
          // through to the redirect below.
          if (HOME_ROUTE === '/') {
            // Initialize org store with bootstrap data (same as catch-all loader)
            const orgs = payload?.data?.orgs;
            const activeOrgId = payload?.data?.activeOrgId;
            if (orgs && Array.isArray(orgs)) {
              const { useOrgStore } = await import('@/stores/orgStore');
              const currentState = useOrgStore.getState();
              const preservedActiveId = currentState.activeOrgId && orgs.some((o: any) => o.id === currentState.activeOrgId)
                ? currentState.activeOrgId
                : (activeOrgId || null);
              if (!currentState.initialized || currentState.orgs.length !== orgs.length) {
                useOrgStore.setState({
                  orgs,
                  activeOrgId: preservedActiveId,
                  initialized: true,
                  isLoading: false,
                  error: null
                });
              }

              // Sync the org's default language to i18n and always ensure dictionary is loaded
              const activeOrg = orgs.find((o: any) => o.id === preservedActiveId) || orgs[0];
              const { setLocale: setI18nLocale, getLocale: getI18nLocale, isDictionaryLoaded, getLocaleOverride } = await import('@/lib/i18n');
              const resolvedLocale = getLocaleOverride() || activeOrg?.default_language || 'en';
              // Always load dictionary before first render (prevents flash of English)
              if (resolvedLocale !== getI18nLocale() || !isDictionaryLoaded()) {
                await setI18nLocale(resolvedLocale);
              }
            }
            return payload?.data?.user || null;
          }
          throw redirect(HOME_ROUTE);
        }
      } catch (e) {
        if (e instanceof Response) {
          // This is a redirect, re-throw it
          throw e;
        }
      }
      // User is not logged in, redirect to auth
      throw redirect('/auth');
    },
    // Attach element only for the HOME_ROUTE === '/' inline-render case.
    // When HOME_ROUTE is any other value, the root loader above redirects
    // and this route never needs to render an element.
    ...(HOME_ROUTE === '/' ? { element: <ProtectedApp /> } : {}),
  },
  {
    path: '/pricing',
    element: <Pricing />,
  },
  {
    path: '/contact',
    element: <Contact />,
  },
  {
    path: '/privacy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/terms',
    element: <TermsOfService />,
  },
  {
    path: '/accept-invite',
    element: <AcceptInvite />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/auth/magic-link/verify',
    element: <MagicLinkVerify />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmail />,
  },
  {
    path: '/auth',
    errorElement: <RouteErrorBoundary />,
    loader: async () => {
      trackNavigation('/auth');
      try {
        const res = await apiFetch('/api/session/bootstrap', { skipRefresh: true });
        if (res.ok) {
          const payload = await res.json().catch(() => ({ data: {} }));
          // Super admin goes straight to admin panel
          const isSuperAdmin = Boolean(payload?.data?.user?.isSuperAdmin);
          if (isSuperAdmin) {
            throw redirect('/admin');
          }
          const active = payload?.data?.activeOrgId || null;
          // Persist active org for services that read from localStorage (for X-Organization-Id header)
          try {
            if (active) localStorage.setItem(storageKey('active_org_id'), active);
          } catch {}
          // Redirect to HOME_ROUTE — product-specific onboarding handled by route guards
          throw redirect(HOME_ROUTE);
        }
      } catch (e) {
        if (e instanceof Response) {
          // This is a redirect, re-throw it
          throw e;
        }
      }
      return null;
    },
    element: <AuthPage />,
  },
  {
    path: '*',
    errorElement: <RouteErrorBoundary />,
    loader: async ({ request }) => {
      const url = new URL(request.url);
      const pathname = url.pathname;
      trackNavigation(pathname);
      
      // Skip bootstrap for static assets
      if (pathname.includes('.') || pathname.startsWith('/assets/')) {
        return null;
      }
      
      try {
        // Allow automatic refresh on protected routes when access token expires
        // skipRedirect allows us to handle the redirect manually in the loader
        let res = await apiFetch('/api/session/bootstrap', {
          skipRedirect: true
        });
        if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] Bootstrap response:', res.status, res.ok);

        // FW9 dogfood fix: retry bootstrap up to 3× with backoff if it 401s.
        // Symptom: navigating to a protected route IMMEDIATELY after login (or
        // after a fast cookie-jar write) can race the access_token cookie
        // propagation. The first bootstrap call hits authRequired before the
        // cookie is sent, gets 401, apiFetch tries refresh which ALSO 401s
        // (refresh_token still propagating), and the loader throws redirect to
        // /auth — page renders blank until the user manually refreshes.
        // PM dogfood reproduced this on /roros/structure-settings AND
        // /roros/decisions AND /roros/control-tower at varying frequencies.
        // 3 attempts at 250ms / 500ms / 1000ms gives 1.75s total upper bound,
        // catches the slow-cookie-write case, and costs zero on the happy path.
        const RETRY_DELAYS_MS = [250, 500, 1000];
        for (const delay of RETRY_DELAYS_MS) {
          if (res.ok) break;
          await new Promise((r) => setTimeout(r, delay));
          res = await apiFetch('/api/session/bootstrap', { skipRedirect: true });
          if (process.env.NODE_ENV === 'development') console.log(`[PROTECTED LOADER] Bootstrap retry (${delay}ms) response:`, res.status, res.ok);
        }

        if (!res.ok) {
          if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] Not authenticated, redirecting to /auth');
          // W10 (AC-PROD-3): Surface NOT_A_MEMBER_OF_THIS_WORKSPACE as a clear message
          // so the user understands why they were rejected (not just a blank redirect).
          let authRedirect = '/auth';
          try {
            const errPayload = await res.clone().json();
            if (
              res.status === 403 &&
              errPayload?.error?.code === 'NOT_A_MEMBER_OF_THIS_WORKSPACE'
            ) {
              const { t: ti18n } = await import('@/lib/i18n');
              const msg = ti18n('You are not a member of this workspace');
              authRedirect = `/auth?error=${encodeURIComponent(msg)}`;
            }
          } catch { /* ignore parse errors — fall through to /auth */ }
          // Clear auth store and every piece of user-scoped client state
          const { useAuthStore } = await import('@/stores/authStore');
          const { resetSessionScopedState } = await import('@/lib/sessionReset');
          resetSessionScopedState();
          useAuthStore.setState({ user: null });
          throw redirect(authRedirect);
        }

        const payload = await res.json().catch(() => ({ data: {} }));
        const { user, orgs, activeOrgId } = payload?.data || {};

        // Session drift guard: if the bootstrapped user differs from the
        // currently stored user, wipe session-scoped state before writing
        // anything new. Catches multi-tab swaps and stale in-memory caches.
        {
          const { useAuthStore } = await import('@/stores/authStore');
          const prevUserId = useAuthStore.getState().user?.id;
          if (prevUserId && user?.id && prevUserId !== user.id) {
            if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] User drift detected, resetting session state', { prev: prevUserId, next: user.id });
            const { resetSessionScopedState } = await import('@/lib/sessionReset');
            resetSessionScopedState();
          }
        }

        // Persist active org for services that read from localStorage (for X-Organization-Id header)
        try {
          if (activeOrgId) localStorage.setItem(storageKey('active_org_id'), activeOrgId);
        } catch {}
        
        // Initialize org store with bootstrap data to prevent redundant fetching
        if (orgs && Array.isArray(orgs)) {
          const { useOrgStore } = await import('@/stores/orgStore');
          const currentState = useOrgStore.getState();

          // Always update the orgs list, but preserve activeOrgId if already set
          // (e.g. super admin just selected an org from the admin panel)
          const preservedActiveId = currentState.activeOrgId && orgs.some((o: any) => o.id === currentState.activeOrgId)
            ? currentState.activeOrgId
            : (activeOrgId || null);

          if (!currentState.initialized || currentState.orgs.length !== orgs.length) {
            useOrgStore.setState({
              orgs,
              activeOrgId: preservedActiveId,
              initialized: true,
              isLoading: false,
              error: null
            });
          }

          // Sync the org's default language to i18n and always ensure dictionary is loaded
          const activeOrg = orgs.find((o: any) => o.id === preservedActiveId) || orgs[0];
          const { setLocale: setI18nLocale, getLocale: getI18nLocale, isDictionaryLoaded, getLocaleOverride } = await import('@/lib/i18n');
          const resolvedLocale = getLocaleOverride() || activeOrg?.default_language || 'en';
          // Always load dictionary before first render (prevents flash of English)
          if (resolvedLocale !== getI18nLocale() || !isDictionaryLoaded()) {
            await setI18nLocale(resolvedLocale);
          }
        }

        // Product-specific onboarding (e.g., ROROS Launchpad) is handled by route guards,
        // not by the boilerplate. No onboarding_completed check needed here.

        return user || null;
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] Caught error:', e);
        if (e instanceof Response) {
          // This is a redirect, re-throw it
          if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] Re-throwing redirect');
          throw e;
        }
        if (process.env.NODE_ENV === 'development') console.log('[PROTECTED LOADER] Non-redirect error, redirecting to /auth');
        throw redirect('/auth');
      }
    },
    element: <ProtectedApp />,
  },
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
      <AppErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <Notifications />
        </QueryClientProvider>
      </AppErrorBoundary>
    </HelmetProvider>
  </React.StrictMode>,
)



